import React from 'react';
import type { HandbookChangeRequest, HandbookPolicy, HandbookPolicyVersion } from '../../types/handbookPolicy';
import {
  diffHandbookDocument,
  diffHandbookText,
  handbookFieldsChanged,
  type HandbookDiffToken,
} from '../../utils/handbookPolicyDiff';

type BaseDoc = Pick<HandbookPolicy, 'title' | 'effectiveDate' | 'location' | 'body'>;

interface HandbookChangeRequestDiffProps {
  proposed: Pick<HandbookChangeRequest, 'title' | 'effectiveDate' | 'location' | 'body'>;
  base: BaseDoc | HandbookPolicyVersion | null;
  authorName?: string;
  /** When true, only render changed hunks so live editing stays scannable. */
  changedOnly?: boolean;
}

function TrackMarks({ tokens }: { tokens: HandbookDiffToken[] }) {
  return (
    <>
      {tokens.map((t, i) => {
        if (t.type === 'same') return <span key={i}>{t.text}{i < tokens.length - 1 ? ' ' : ''}</span>;
        if (t.type === 'del') {
          return (
            <del key={i} className="people-handbook-tc-del">
              {t.text}
            </del>
          );
        }
        return (
          <ins key={i} className="people-handbook-tc-ins">
            {t.text}
          </ins>
        );
      })}
    </>
  );
}

const HandbookChangeRequestDiff: React.FC<HandbookChangeRequestDiffProps> = ({
  proposed,
  base,
  authorName,
  changedOnly = false,
}) => {
  const before = {
    title: base?.title || '',
    effectiveDate: base?.effectiveDate || '',
    location: base?.location || '',
    body: base?.body || '',
  };
  const after = {
    title: proposed.title,
    effectiveDate: proposed.effectiveDate,
    location: proposed.location,
    body: proposed.body,
  };
  const fields = handbookFieldsChanged(before, after).filter(row => row.field !== 'Body');
  const allBlocks = diffHandbookDocument(before.body, after.body);
  const blocks = changedOnly ? allBlocks.filter(b => b.changed) : allBlocks;
  const bodyChanged = allBlocks.some(b => b.changed);

  if (fields.length === 0 && !bodyChanged) {
    return (
      <div className="people-handbook-tc" role="status">
        <header className="people-handbook-tc-toolbar">
          <div>
            <h3 className="people-handbook-rail-heading">Tracked changes</h3>
            {authorName ? <p className="people-handbook-tc-author">{authorName}</p> : null}
          </div>
        </header>
        <p className="people-hub-muted">
          No tracked changes yet. Edit the policy — including chip labels or URLs — to see insertions and deletions.
        </p>
      </div>
    );
  }

  return (
    <article className="people-handbook-tc" aria-label="Tracked changes">
      <header className="people-handbook-tc-toolbar">
        <div>
          <h3 className="people-handbook-rail-heading">Tracked changes</h3>
          {authorName ? <p className="people-handbook-tc-author">{authorName}</p> : null}
        </div>
        <div className="people-handbook-tc-legend" aria-hidden>
          <span className="people-handbook-tc-del">Deleted</span>
          <span className="people-handbook-tc-ins">Inserted</span>
        </div>
      </header>

      {fields.length > 0 ? (
        <dl className="people-handbook-tc-meta">
          {fields.map(row => (
            <div key={row.field} className="people-handbook-tc-meta-row people-handbook-tc-changed">
              <dt>{row.field}</dt>
              <dd>
                <TrackMarks tokens={diffHandbookText(row.before === '—' ? '' : row.before, row.after === '—' ? '' : row.after)} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {bodyChanged || blocks.length > 0 ? (
        <div className="people-handbook-tc-doc">
          {blocks.map((block, i) => (
            <p key={i} className={`people-handbook-tc-block${block.changed ? ' people-handbook-tc-changed' : ''}`}>
              <TrackMarks tokens={block.tokens} />
            </p>
          ))}
        </div>
      ) : null}
    </article>
  );
};

export default HandbookChangeRequestDiff;
